import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	Divider,
	Paper,
	Skeleton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ArrowDownwardRounded,
	ArrowUpwardRounded,
	AssignmentTurnedInOutlined,
	EngineeringOutlined,
	ErrorOutlineRounded,
	InfoOutlined,
	PriorityHighRounded,
	ReportOutlined,
	SupportAgentOutlined,
	TaskAltOutlined,
	WarningAmberRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useCentralAlertas } from "../context";
import {
	TIPOS_EVENTO,
	LABEL_TIPO_EVENTO,
	LABEL_SEVERIDADE,
} from "../../../utils/centralDeAlertas/labels";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

const PAGINA_EVENTOS = 50;

// Ícone de cada tipo de evento (mesmo ícone na linha e nos chips — chips
// sempre com ícone + rótulo, nunca cor sozinha).
const ICONE_TIPO = {
	queda: ArrowDownwardRounded,
	recuperacao: ArrowUpwardRounded,
	atendimento_aberto: SupportAgentOutlined,
	atendimento_fechado: TaskAltOutlined,
	os_criada: EngineeringOutlined,
	os_concluida: AssignmentTurnedInOutlined,
};

// Ícones de severidade no padrão do severityChip do sistema.
const ICONE_SEVERIDADE = {
	critico: WarningAmberRounded,
	alto: ErrorOutlineRounded,
	medio: ReportOutlined,
	baixo: PriorityHighRounded,
	informativo: InfoOutlined,
};

// Feed cronológico de todos os eventos do período (mais recente primeiro),
// agrupado por dia com cabeçalho sticky, filtrável por tipo de evento e com
// paginação incremental. Clicar num evento vinculado a incidente foca a
// linha correspondente na TabelaIncidentes (focarIncidente do context).
const TimelineGlobal = () => {
	const theme = useTheme();
	const styles = useStyles(theme);
	const { agora, carregando, eventosFiltrados, focarIncidente } =
		useCentralAlertas();

	// Tipos de evento ativos nos chips de filtro (todos ativos por padrão).
	const [tiposAtivos, setTiposAtivos] = useState(() => new Set(TIPOS_EVENTO));
	const [limite, setLimite] = useState(PAGINA_EVENTOS);

	// Reinicia a paginação quando os eventos ou os chips mudam.
	useEffect(() => {
		setLimite(PAGINA_EVENTOS);
	}, [eventosFiltrados, tiposAtivos]);

	const alternarTipo = (tipo) => {
		setTiposAtivos((atuais) => {
			const proximos = new Set(atuais);
			if (proximos.has(tipo)) proximos.delete(tipo);
			else proximos.add(tipo);
			return proximos;
		});
	};

	// Eventos do context (já filtrados pelo dashboard) cortados pelos chips.
	const eventosVisiveis = useMemo(
		() => eventosFiltrados.filter((evento) => tiposAtivos.has(evento.tipo)),
		[eventosFiltrados, tiposAtivos]
	);

	const eventosExibidos = useMemo(
		() => eventosVisiveis.slice(0, limite),
		[eventosVisiveis, limite]
	);

	// Agrupa os eventos exibidos por dia, preservando a ordem (desc).
	const grupos = useMemo(() => {
		const rotuloDia = (dataHora) => {
			const data = dayjs(dataHora);
			if (data.isSame(agora, "day")) return "Hoje";
			if (data.isSame(agora.subtract(1, "day"), "day")) return "Ontem";
			return data.format("DD [de] MMMM");
		};

		const lista = [];
		let grupoAtual = null;
		eventosExibidos.forEach((evento) => {
			const chave = dayjs(evento.dataHora).format("YYYY-MM-DD");
			if (!grupoAtual || grupoAtual.chave !== chave) {
				grupoAtual = { chave, rotulo: rotuloDia(evento.dataHora), eventos: [] };
				lista.push(grupoAtual);
			}
			grupoAtual.eventos.push(evento);
		});
		return lista;
	}, [eventosExibidos, agora]);

	const haMais = eventosVisiveis.length > eventosExibidos.length;

	const renderEvento = (evento) => {
		const clicavel = Boolean(evento.incidenteId);
		const IconeEvento = ICONE_TIPO[evento.tipo];
		const IconeSeveridade =
			evento.severidade != null ? ICONE_SEVERIDADE[evento.severidade] : null;

		return (
			<Tooltip
				key={evento.id}
				title={clicavel ? "Ver incidente na tabela" : ""}
				arrow
			>
				<Box
					sx={styles.linhaEvento(clicavel)}
					onClick={
						clicavel ? () => focarIncidente(evento.incidenteId) : undefined
					}
				>
					{IconeEvento && <IconeEvento sx={styles.iconeEvento(evento.tipo)} />}
					<Typography variant="caption" sx={styles.hora}>
						{dayjs(evento.dataHora).format("HH:mm")}
					</Typography>
					<Box sx={styles.conteudoEvento}>
						<Box sx={styles.linhaChips}>
							<Chip
								size="small"
								icon={IconeEvento ? <IconeEvento /> : undefined}
								label={LABEL_TIPO_EVENTO[evento.tipo]}
								sx={styles.chipTipoEvento(evento.tipo)}
							/>
							{evento.severidade != null && (
								<Chip
									size="small"
									icon={IconeSeveridade ? <IconeSeveridade /> : undefined}
									label={LABEL_SEVERIDADE[evento.severidade]}
									sx={styles.chipSeveridade(evento.severidade)}
								/>
							)}
							<Typography variant="caption" sx={styles.textoOrigem}>
								{`${evento.clienteNome} · ${evento.equipamentoNome}`}
							</Typography>
						</Box>
						{/* 				<Tooltip title={evento.resumo} arrow placement="top">
							<Typography sx={styles.resumo}>{evento.resumo}</Typography>
						</Tooltip> */}
					</Box>
				</Box>
			</Tooltip>
		);
	};

	const renderConteudo = () => {
		if (carregando) {
			return (
				<>
					<Box sx={styles.chipsFiltro}>
						{TIPOS_EVENTO.map((tipo) => (
							<Skeleton
								key={tipo}
								variant="rounded"
								width={90}
								height={20}
								sx={styles.skeletonChip}
							/>
						))}
					</Box>
					<Box sx={styles.listaEventos}>
						{[0, 1, 2, 3, 4, 5, 6, 7].map((indice) => (
							<Box key={indice} sx={styles.skeletonLinha}>
								<Skeleton variant="circular" width={18} height={18} />
								<Skeleton variant="text" width={38} />
								<Box sx={styles.skeletonTextos}>
									<Skeleton
										variant="rounded"
										width="45%"
										height={20}
										sx={styles.skeletonChip}
									/>
									<Skeleton variant="text" width="80%" />
								</Box>
							</Box>
						))}
					</Box>
				</>
			);
		}

		return (
			<>
				<Box sx={styles.chipsFiltro}>
					{TIPOS_EVENTO.map((tipo) => {
						const ativo = tiposAtivos.has(tipo);
						const IconeEvento = ICONE_TIPO[tipo];
						return (
							<Chip
								key={tipo}
								size="small"
								variant={ativo ? "filled" : "outlined"}
								icon={IconeEvento ? <IconeEvento /> : undefined}
								label={LABEL_TIPO_EVENTO[tipo]}
								onClick={() => alternarTipo(tipo)}
								sx={styles.chipFiltro(tipo, ativo)}
							/>
						);
					})}
				</Box>
				{!eventosVisiveis.length ? (
					<Box sx={styles.estadoVazio}>
						<Typography color="text.secondary">
							Nenhum evento no período selecionado.
						</Typography>
					</Box>
				) : (
					<Box sx={styles.listaEventos}>
						{grupos.map((grupo) => (
							<Box key={grupo.chave}>
								<Box sx={styles.cabecalhoDia}>
									<Typography
										variant="caption"
										sx={styles.textoCabecalhoDia}
									>
										{grupo.rotulo}
									</Typography>
								</Box>
								{grupo.eventos.map((evento, indice) => (
									<React.Fragment key={evento.id}>
										{indice > 0 && <Divider sx={styles.divisorEventos} />}
										{renderEvento(evento)}
									</React.Fragment>
								))}
							</Box>
						))}
						{haMais && (
							<Box sx={styles.rodapePaginacao}>
								<Typography variant="caption" sx={styles.textoPaginacao}>
									{`Exibindo ${eventosExibidos.length} de ${eventosVisiveis.length} eventos`}
								</Typography>
								<Button
									size="small"
									sx={styles.botaoCarregarMais}
									onClick={() =>
										setLimite((atual) => atual + PAGINA_EVENTOS)
									}
								>
									Carregar mais 50
								</Button>
							</Box>
						)}
					</Box>
				)}
			</>
		);
	};

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.linhaCabecalho}>
				<Box sx={styles.header}>
					<Typography variant="mySubtitle">Linha do tempo global</Typography>
					<Typography variant="caption" sx={styles.caption}>
						Todos os eventos do período
					</Typography>
				</Box>
				<MetricInfo theme={theme} guide={GUIDES_CENTRAL.timelineGlobal} />
			</Box>
			{renderConteudo()}
		</Paper>
	);
};

export default TimelineGlobal;
